import { useCallback, useMemo, useState } from 'react'
import { Alert, Avatar, Form, Input, Modal, Radio, Select, Tooltip } from 'antd'
import { CheckOutlined, PlusOutlined, RobotOutlined, ShopOutlined, SolutionOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons'
import type { AvatarKey, InstanceDirectory } from '@/services/instanceAdministration'
import type { RoleInput } from '../hooks/useRoles'
import type { useConfiguration } from '../hooks/useConfiguration'

const AVATARS = { user: <UserOutlined />, team: <TeamOutlined />, shop: <ShopOutlined />,
  solution: <SolutionOutlined />, robot: <RobotOutlined /> }
const AVATAR_LABELS: Record<AvatarKey, string> = { user: '用户', team: '团队', shop: '商店', solution: '名片', robot: '机器人' }
type Role = InstanceDirectory['instances'][number]

const RoleCard = ({ role, selected, onSelect }: { role: Role; selected: boolean; onSelect: (id: string) => void }) => {
  const select = useCallback(() => onSelect(role.instance_id), [onSelect, role.instance_id])
  return <button type="button" role="radio" aria-checked={selected} aria-label={role.display_name ?? '未配置名称'}
    disabled={!role.enabled} onClick={select} className={`role-card${selected ? ' role-card--selected' : ''}`}>
    <Avatar size={42} icon={AVATARS[role.avatar_key ?? 'robot']} />
    <span className="role-card__name">{role.display_name ?? '未配置名称'}</span>
    {selected ? <CheckOutlined className="role-card__check" /> : null}
  </button>
}

export const RoleCards = ({ model }: { model: ReturnType<typeof useConfiguration> }) => {
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm<RoleInput>()
  const { create, pending, error } = model.roleCreator
  const { selectInstance } = model
  const creationAvailable = model.directory.data?.role_creation_available === true
  const sources = useMemo(() => model.directory.data?.instances.filter((item) => item.enabled).map((item) => ({
    value: item.instance_id, label: item.display_name ?? '未配置名称' })) ?? [], [model.directory.data])
  const show = useCallback(() => {
    form.resetFields(); form.setFieldsValue({ source_instance_id: model.instanceId, avatar_key: 'robot' }); setOpen(true)
  }, [form, model.instanceId])
  const close = useCallback(() => { if (!pending) setOpen(false) }, [pending])
  const submit = useCallback(async (values: RoleInput) => {
    try { await create(values); setOpen(false) } catch { /* Retain input for the same operation's retry. */ }
  }, [create])
  const confirm = useCallback(() => form.submit(), [form])
  return <>
    <div className="role-cards" role="radiogroup" aria-label="智能体角色">
      {model.directory.data?.instances.map((role) => <RoleCard key={role.instance_id} role={role}
        selected={role.instance_id === model.instanceId} onSelect={selectInstance} />)}
      {creationAvailable ? <button type="button" aria-label="新增角色" className="role-card role-card--add" onClick={show} disabled={!sources.length || model.loading}>
        <PlusOutlined /><span>新增角色</span>
      </button> : null}
    </div>
    <Modal title="新增智能体角色" open={open && creationAvailable} onCancel={close} onOk={confirm} okText="创建角色" cancelText="取消"
      confirmLoading={pending} closable={!pending} mask={{ closable: !pending }} destroyOnHidden>
      <Form form={form} layout="vertical" onFinish={submit} disabled={pending} preserve={false}>
        <Form.Item name="display_name" label="角色名称" rules={[{ required: true, whitespace: true, max: 128, message: '请填写角色名称' }]}>
          <Input autoComplete="off" maxLength={128} />
        </Form.Item>
        <Form.Item name="source_instance_id" label="复制已应用配置" rules={[{ required: true, message: '请选择来源角色' }]}>
          <Select options={sources} />
        </Form.Item>
        <Form.Item name="avatar_key" label="头像" rules={[{ required: true }]}>
          <Radio.Group className="role-avatar-options">
            {Object.entries(AVATARS).map(([key, icon]) => <Tooltip title={AVATAR_LABELS[key as AvatarKey]} key={key}>
              <Radio.Button value={key} aria-label={AVATAR_LABELS[key as AvatarKey]}>{icon}</Radio.Button>
            </Tooltip>)}
          </Radio.Group>
        </Form.Item>
        {error ? <Alert type="error" showIcon title={error.message} role="alert" /> : null}
      </Form>
    </Modal>
  </>
}
